import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CURRENCY_LABELS,
  PAYMENT_CURRENCIES,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  PRESCRIPTION_REVIEW_LABELS,
  PRESCRIPTION_REVIEW_STATUSES,
  type PaymentMethod,
  type PrescriptionHistoryEvent,
  type PrescriptionReview,
  type PaymentCurrency,
  type Prospect,
  type ProspectEdit,
} from '@joker/contracts'
import type { AbandonedCart } from '@joker/contracts'
import { useTRPC } from '../trpc'
import { Panel, Tile, Loading, ErrorNote, ViewTitle, fmtDate, DateParts } from '../ui'
import { displayPhone, e164 } from '../phone'
import { ScannedPrescriptions } from './ScannedPrescriptions'

import { GradeChip } from './Jobs'

/* Sorted best first: a screenful of leads is a call list. */
const ELIGIBILITY_ORDER: Record<string, number> = { suitable: 1, needs_answers: 2, refer: 3, incomplete: 4, not_suitable: 5 }

type Col = 'code' | 'name' | 'email' | 'phone' | 'joined' | 'paidOn' | 'price' | 'sessions' | 'source' | 'rx' | 'rxReview' | 'eligibility' | 'emails'

/**
 * Leads: people who asked for the product, and what became of them.
 *
 * The table stays deliberately thin — who they are and when they asked — because
 * it is read by scanning. Everything else about one person, and everything that
 * changes them, is on their own view: a table carrying a form builder's worth of
 * controls is a table nobody can read.
 */
export function Prospects({
  onOpenPerson,
  bucketId,
  onClearBucket,
  onOpenPurchases,
  onOpenOrder,
}: {
  onOpenPerson: (id: string) => void
  onOpenOrder?: (id: string) => void
  bucketId?: string
  onClearBucket: () => void
  /* Leads holds everyone who has not paid; once they pay, their order is on
     the Purchases screen. */
  onOpenPurchases?: () => void
}) {
  const trpc = useTRPC()
  const q = useQuery(trpc.prospects.list.queryOptions(bucketId ? { bucketId } : {}))
  const buckets = useQuery(trpc.buckets.list.queryOptions())
  const [leadsSheet, setLeadsSheet] = useState<'waiting' | 'onboarded' | 'converted' | 'abandoned' | 'cartsConverted'>('waiting')
  const showing = leadsSheet
  const abandoned = useQuery(trpc.prospects.abandoned.queryOptions())
  const abandonedReal = (abandoned.data ?? []).filter((c) => !c.isTest).length
  const cartsConverted = (abandoned.data ?? []).filter((c) => !c.isTest && c.afterOrder).length
  /* Converted reads newest purchase first; the other sheets newest ask first. */
  const setShowing = (s: typeof leadsSheet) => {
    setLeadsSheet(s)
    setSort({ col: s === 'converted' ? 'paidOn' : 'joined', dir: 'desc' })
  }
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<{ col: Col; dir: 'asc' | 'desc' }>({ col: 'joined', dir: 'desc' })
  /* Leads whose emails are unfolded under their row. */
  const [openEmails, setOpenEmails] = useState<ReadonlySet<string>>(new Set())
  const toggleEmails = (id: string) =>
    setOpenEmails((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  if (q.error) return <ErrorNote error={q.error} />
  if (!q.data) return <Loading what="leads" />


  /* An optometrist enquiry is a different channel, not a different tab here:
     a practice asking about carrying the programme has nothing to do with the
     patient funnel, so those leads live on the Optometrists screen and are
     excluded outright rather than counted and hidden. */
  const patients = q.data.filter((p) => p.leadKind !== 'optometrist')
  const waiting = patients.filter((p) => p.waiting)
  const converted = patients.filter((p) => !p.waiting && p.paid)
  const onboardedUnpaid = patients.filter((p) => !p.waiting && !p.paid)

  /* The two ways in — the waitlist and an abandoned cart — counted as people,
     once each: somebody on the waitlist who also left a cart is one person in
     the total. A cart from somebody who had already bought is a customer
     looking again, not a lead, so it is left out; so are our own tests.
     Refunded buyers do not count as converted. */
  const key = (id: string | null, email: string | null) => id ?? `email:${(email ?? '').toLowerCase()}`
  const waitlistKeys = new Set(patients.map((p) => p.id))
  const waitlistWon = new Set(converted.filter((p) => !refunded(p)).map((p) => p.id))
  const cartLeads = (abandoned.data ?? []).filter((c) => !c.isTest && !(c.boughtBefore && !c.boughtAfter))
  const cartKeys = new Set(cartLeads.map((c) => key(c.personId, c.email)))
  const cartWon = new Set(cartLeads.filter((c) => c.afterOrder).map((c) => key(c.personId, c.email)))
  const everyone = new Set([...waitlistKeys, ...cartKeys])
  const everyoneWon = new Set([...waitlistWon, ...cartWon])
  const pctOf = (n: number, of: number) => (of === 0 ? '0%' : `${Math.round((n / of) * 100)}%`)
  const cartsReady = abandoned.data != null

  const matches = (p: Prospect) => {
    const s = search.trim().toLowerCase()
    if (!s) return true
    return [p.code, p.name, p.email, p.phone, p.source, p.note, p.whoIAm, p.message, p.practiceName, p.lastEmailSubject]
      .some((v) => (v ?? '').toLowerCase().includes(s))
  }

  const prepare = (list: Prospect[]) => {
    const value = (p: Prospect): string | number => {
      switch (sort.col) {
        case 'code': return p.code ?? ''
        case 'name': return (p.name ?? '').toLowerCase()
        case 'email': return (p.email ?? '').toLowerCase()
        case 'phone': return p.phone ?? ''
        /* The full instant now that Klaviyo's joining time is kept, so a day's
           signups come out in the order they arrived. A lead known only by the
           day sits at that day's midnight, and the name still breaks a tie. */
        case 'joined': return p.joinedAt ?? ''
        case 'paidOn': return (p.convertedAt ?? '').slice(0, 10)
        case 'price': return p.pricePaidCents ?? -1
        case 'sessions': return p.sessionsTotal
        case 'source': return (p.source ?? '').toLowerCase()
        case 'rx': return p.hasPrescription || p.prescriptionCount > 0 ? 1 : 0
        /* In the order the options are listed, so Approved sorts next to Not
           reviewed rather than alphabetically among the rest. */
        case 'rxReview': return PRESCRIPTION_REVIEW_STATUSES.indexOf(p.prescriptionReview)
        case 'eligibility': return p.eligibility ? (ELIGIBILITY_ORDER[p.eligibility] ?? 9) : ''
        case 'emails': return p.emailsSent
        default: return ''
      }
    }
    const dir = sort.dir === 'asc' ? 1 : -1
    /* Ties break on the name, always A-Z whichever way the column points: a
       screenful of people who all asked on the same day is a list to read down,
       and reversing the names with the dates would make it unreadable. */
    const byName = (a: Prospect, b: Prospect) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })

    return list.filter(matches).sort((a, b) => {
      const x = value(a)
      const y = value(b)
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir || byName(a, b)
      /* Blanks last either way: an empty cell is the absence of a value, not the
         smallest one. */
      if (x === '' && y !== '') return 1
      if (y === '' && x !== '') return -1
      return String(x).localeCompare(String(y)) * dir || byName(a, b)
    })
  }

  const onSort = (col: Col) =>
    setSort((s) =>
      s.col === col
        ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: col === 'name' || col === 'email' || col === 'source' ? 'asc' : 'desc' },
    )

  const exportHref = `/api/export/prospects.csv?${new URLSearchParams({
    group: showing,
    ...(search.trim() ? { search: search.trim() } : {}),
  }).toString()}`

  const th = (col: Col, label: string, right = false) => (
    <th
      className={`${right ? 'r ' : ''}sortable`}
      aria-sort={sort.col === col ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" onClick={() => onSort(col)}>
        {label}
        {sort.col === col && <span aria-hidden="true"> {sort.dir === 'asc' ? '↑' : '↓'}</span>}
      </button>
    </th>
  )

  const row = (p: Prospect, columns: number, withOrder = false) => (
    <Fragment key={p.id}>
    <tr
      tabIndex={0}
      onClick={() => onOpenPerson(p.id)}
      onKeyDown={(e) => e.key === 'Enter' && onOpenPerson(p.id)}
    >
      <td className="num">{p.code ?? '—'}</td>
      <td>
        <strong>{p.name ?? <span className="muted">Unnamed</span>}</strong>
      </td>
      <td>{p.hasPrescription || p.prescriptionCount > 0 ? 'Yes' : <span className="muted">No</span>}</td>
      <td><GradeChip grade={p.eligibility} override={p.eligibilityIsOverride} /></td>
      <td>
        {p.prescriptionReview === 'not_reviewed' ? (
          <span className="muted">{PRESCRIPTION_REVIEW_LABELS.not_reviewed}</span>
        ) : (
          PRESCRIPTION_REVIEW_LABELS[p.prescriptionReview]
        )}
      </td>
      <td>{p.email ?? <span className="muted">—</span>}</td>
      <td className="num">{displayPhone(p.phone) || <span className="muted">—</span>}</td>
      <td className="num">
        {fmtDate(p.joinedAt)}
        {askedTime(p.joinedAt) && (
          <>
            <br />
            <span className="muted sub-line">{askedTime(p.joinedAt)}</span>
          </>
        )}
      </td>
      <td className="lead-emails">
        {p.emailsSent === 0 ? (
          <span className="muted">None</span>
        ) : (
          <>
            <button
              type="button"
              className="linklike"
              aria-expanded={openEmails.has(p.id)}
              title="Show every email Klaviyo sent them"
              onClick={(e) => {
                e.stopPropagation()
                toggleEmails(p.id)
              }}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {openEmails.has(p.id) ? '▾' : '▸'} {p.emailsSent} received
              {p.emailsOpened > 0 ? ` · ${p.emailsOpened} opened` : ''}
            </button>
            {p.emailUnsubscribed && <span className="down"> · unsubscribed</span>}
            {p.lastEmailSubject && (
              <>
                <br />
                <span className="muted sub-line" title={p.lastEmailSubject}>
                  Last: {p.lastEmailSubject}
                </span>
              </>
            )}
          </>
        )}
      </td>
      {!p.waiting && <td className="num">{fmtDate(p.convertedAt)}</td>}
      {!p.waiting && (
        <td className="r num">
          {/* Paid in more than one currency: no native figure is true, so only
              the dollar total is shown. */}
          {p.paidCurrencyCount > 1 ? (
            money(p.pricePaidUsdCents, 'USD') ?? <span className="muted">—</span>
          ) : (
            <>
              {money(p.pricePaidCents, p.currency) ?? <span className="muted">—</span>}
              {p.pricePaidUsdCents != null && p.currency !== 'USD' && (
                <>
                  <br />
                  <span className="muted sub-line">≈ {money(p.pricePaidUsdCents, 'USD')}</span>
                </>
              )}
            </>
          )}
        </td>
      )}
      {!p.waiting && <td className="r num">{p.sessionsTotal}</td>}
      {withOrder && (
        <td className="num">
          {p.wixOrderNumbers.length === 0 ? (
            <span className="muted">—</span>
          ) : (
            p.wixOrderNumbers.map((n) => `#${n}`).join(', ')
          )}
          {refunded(p) && <span className="down"> · refunded</span>}
          {daysToBuy(p) != null && (
            <>
              <br />
              <span className="muted sub-line">{daysToBuy(p) === 0 ? 'same day as asking' : `${daysToBuy(p)} d after asking`}</span>
            </>
          )}
        </td>
      )}
      <td>{p.source ?? <span className="muted">—</span>}</td>
    </tr>
    {openEmails.has(p.id) && (
      <tr className="lead-emails-open">
        <td colSpan={columns}>
          <LeadEmails personId={p.id} />
        </td>
      </tr>
    )}
    </Fragment>
  )

  const table = (list: Prospect[], nameHeader: string, full: boolean, withOrder = false) => (
    <div className="scroll-x">
      <table className="data">
        <thead>
          <tr>
            {th('code', 'ID')}
            {th('name', nameHeader)}
            {th('rx', 'Prescription')}
            {th('eligibility', 'Eligibility')}
            {th('rxReview', 'Prescription approval')}
            {th('email', 'Email')}
            {th('phone', 'Phone')}
            {th('joined', 'Asked on')}
            {th('emails', 'Klaviyo emails')}
            {full && th('paidOn', 'Paid on')}
            {full && th('price', 'Paid', true)}
            {full && th('sessions', 'Sessions', true)}
            {withOrder && <th>Wix order</th>}
            {th('source', 'Source')}
          </tr>
        </thead>
        <tbody>{prepare(list).map((p) => row(p, (full ? 13 : 10) + (withOrder ? 1 : 0), withOrder))}</tbody>
      </table>
    </div>
  )

  const empty = (what: string) => (
    <p className="footnote">{search ? 'Nobody matches that search.' : what}</p>
  )

  return (
    <>
      <ViewTitle suit="hand">Leads</ViewTitle>
      <p className="view-sub">
        People who asked for the product through the website form or the Klaviyo waitlist and have
        not paid. Nobody waiting has an Oryx account or a payment, and none of them is counted
        anywhere else in Joker. Once somebody pays, their order is in <strong>Direct purchases</strong>. Press a
        row to open somebody.
      </p>

      <div className="tiles">
        <Tile k="Total" v={cartsReady ? everyone.size : '…'} d="waitlist and carts, each person once" />
        <Tile k="Waitlist" v={patients.length} d={`${waiting.length} still waiting`} />
        <Tile
          k="Abandoned cart"
          v={cartsReady ? cartKeys.size : '…'}
          d={
            <button type="button" className="linklike" onClick={() => setShowing('abandoned')}>
              left a cart, not yet customers →
            </button>
          }
        />
        <Tile
          k="Waitlist converted"
          v={`${waitlistWon.size} · ${pctOf(waitlistWon.size, patients.length)}`}
          d={
            <button type="button" className="linklike" onClick={() => setShowing('converted')}>
              see who bought →
            </button>
          }
        />
        <Tile
          k="Cart converted"
          v={cartsReady ? `${cartWon.size} · ${pctOf(cartWon.size, cartKeys.size)}` : '…'}
          d={
            <button type="button" className="linklike" onClick={() => setShowing('cartsConverted')}>
              see who came back →
            </button>
          }
        />
        <Tile
          k="Total converted"
          v={cartsReady ? `${everyoneWon.size} · ${pctOf(everyoneWon.size, everyone.size)}` : '…'}
          d="bought, refunds left out"
        />
      </div>

      {bucketId && (
        <div className="toolbar">
          <button type="button" className="tag" onClick={onClearBucket}>
            Bucket: {buckets.data?.find((b) => b.id === bucketId)?.name ?? '…'}{' '}
            <span aria-hidden="true">✕</span>
          </button>
        </div>
      )}

      <div className="seg seg-inline" role="tablist" aria-label="Which leads">
        <button type="button" role="tab" aria-selected={showing === 'waiting'} onClick={() => setShowing('waiting')}>
          Waiting · {waiting.length}
        </button>
        <button type="button" role="tab" aria-selected={showing === 'onboarded'} onClick={() => setShowing('onboarded')}>
          Onboarded, unpaid · {onboardedUnpaid.length}
        </button>
        <button type="button" role="tab" aria-selected={showing === 'converted'} onClick={() => setShowing('converted')}>
          Converted · {converted.length}
        </button>
        <button type="button" role="tab" aria-selected={showing === 'abandoned'} onClick={() => setShowing('abandoned')}>
          Abandoned carts{abandoned.data ? ` · ${abandonedReal}` : ''}
        </button>
        <button type="button" role="tab" aria-selected={showing === 'cartsConverted'} onClick={() => setShowing('cartsConverted')}>
          Cart conversions{abandoned.data ? ` · ${cartsConverted}` : ''}
        </button>
      </div>

      {showing === 'abandoned' && (
        <AbandonedCarts carts={abandoned.data} error={abandoned.error} onOpenPerson={onOpenPerson} />
      )}

      {showing === 'cartsConverted' && (
        <CartConversions
          carts={abandoned.data}
          error={abandoned.error}
          onOpenPerson={onOpenPerson}
          {...(onOpenOrder ? { onOpenOrder } : {})}
        />
      )}

      {showing !== 'abandoned' && showing !== 'cartsConverted' && (
      <div className="toolbar prospect-search">
        <input
          type="search"
          placeholder="Name, email, phone, source, message…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search leads"
        />
        {search && (
          <button type="button" className="btn ghost sm" onClick={() => setSearch('')}>
            Clear
          </button>
        )}
        {/* A plain link, so the browser saves the file itself. It carries the tab
            and the search that produced the table, not the rows on screen. */}
        <a className="btn primary export" href={exportHref} download>
          ↓ Export CSV
        </a>
      </div>
      )}

      {showing === 'waiting' && (
        <Panel
          title="Still waiting"
          note={search ? `${prepare(waiting).length} of ${waiting.length} match` : `${waiting.length} people`}
        >
          {prepare(waiting).length === 0 ? empty('Nobody waiting.') : table(waiting, 'Name', false)}
        </Panel>
      )}

      {showing === 'converted' && <ConvertedSummary converted={converted} />}

      {showing === 'converted' && (
        <Panel
          title="Converted"
          note={
            search
              ? `${prepare(converted).length} of ${converted.length} match`
              : `${converted.filter((p) => !refunded(p)).length} bought · ${
                  converted.filter((p) => !refunded(p) && recent(p.convertedAt, 30)).length
                } in the last 30 days${
                  converted.some(refunded) ? ` · ${converted.filter(refunded).length} refunded` : ''
                }`
          }
        >
          {prepare(converted).length === 0
            ? empty('Nobody has bought yet.')
            : table(converted, 'Buyer', true, true)}
          <p className="footnote">
            Leads who went on to pay, matched by the email they used on the waitlist. Somebody who
            checked out under another address is still under Waiting until{' '}
            <em>Connect to a patient record…</em> joins the two. Their orders are also in{' '}
            {onOpenPurchases ? (
              <button type="button" className="linklike" onClick={onOpenPurchases}>
                Direct purchases
              </button>
            ) : (
              'Direct purchases'
            )}
            .
          </p>
        </Panel>
      )}

      {showing === 'onboarded' && (
        <Panel
          title="Onboarded, unpaid"
          note={
            search
              ? `${prepare(onboardedUnpaid).length} of ${onboardedUnpaid.length} match`
              : `${onboardedUnpaid.length} people`
          }
        >
          {prepare(onboardedUnpaid).length === 0
            ? empty('Nobody in between.')
            : table(onboardedUnpaid, 'Patient', true)}
          <p className="footnote">
            They asked, got an Oryx account, and have bought nothing. Neither waiting nor paid, so
            counted in neither figure — and the group most worth chasing, because the hard part is
            already done.
          </p>
        </Panel>
      )}
    </>
  )
}

/**
 * The time somebody joined, in the viewer's own clock, or null when only the
 * day is known. A day on its own is stored as midnight (an import) or midday (a
 * hand edit) UTC, and printing either as a time would invent one.
 */
export function askedTime(iso: string | null): string | null {
  if (!iso || /T(00|12):00:00(\.000)?Z$/.test(iso)) return null
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

function money(cents: number | null, currency: string | null): string | null {
  return cents == null
    ? null
    : new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currencyDisplay: 'narrowSymbol',
        currency: currency || 'USD',
        maximumFractionDigits: 0,
      }).format(cents / 100)
}

/* ------------------------------ one person ------------------------------ */

/**
 * One lead: everything said about them, and everything that can be done to them. Read-only until Edit is pressed, so opening somebody to read
 * their message cannot change them by accident.
 */
/**
 * The lead half of anybody's record, shown on the person page: what they said
 * when they asked and how to correct it, then their orders, prescription and
 * the emails Klaviyo sent. Somebody who never was a lead gets everything but
 * the lead details, so a direct Wix buyer can still have a prescription.
 */
export function LeadSections({
  prospect,
  onChanged,
  onOpenOrder,
}: {
  prospect: Prospect
  /** After a change that may move them (spam, a merge), so the page can follow. */
  onChanged?: () => void
  onOpenOrder?: (id: string) => void
}) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)

  const after = async () => {
    await qc.invalidateQueries()
    setLinking(false)
    setPaying(false)
    onChanged?.()
  }
  const [linking, setLinking] = useState(false)
  const [paying, setPaying] = useState(false)
  const setSpam = useMutation(trpc.prospects.setSpam.mutationOptions({ onSuccess: after }))
  const isLead = Boolean(prospect.code || prospect.source || prospect.joinedAt || prospect.message || prospect.whoIAm)

  return (
    <>
      {!isLead ? null : editing ? (
        <ProspectEditor prospect={prospect} onDone={() => setEditing(false)} />
      ) : (
        <Panel title="Lead" note={[prospect.code, prospect.source].filter(Boolean).join(' · ') || undefined}>
          {/* Only what is known: an empty field is a gap in the grid, and the
              editor is where to fill it. Money, orders, sessions and practice
              are on their own panels and in the heading, so not repeated here. */}
          <div className="facts">
            <Fact k="Email" v={prospect.email} copy={prospect.email} />
            <Fact k="Phone" v={displayPhone(prospect.phone) || null} copy={e164(prospect.phone, prospect.location)} />
            <Fact k="Age range" v={prospect.ageRange} />
            <Fact k="Prescription in last 6 months" v={yesNo(prospect.hasRecentPrescription)} />
            <Fact
              k="Where"
              v={[prospect.city, [prospect.state, prospect.postalCode].filter(Boolean).join(' '), prospect.country]
                .filter(Boolean)
                .join(', ') || null}
            />
            <Fact k="Outside the US" v={yesNo(prospect.outsideUs)} />
            <Fact k="Location" v={prospect.location} />
            <Fact k="Who they are" v={prospect.whoIAm} />
            <Fact k="Waitlist page" v={prospect.campaign} />
            <Fact k="Promo" v={prospect.promo} />
            <Fact k="Mailing address" v={prospect.mailingAddress} copy={prospect.mailingAddress} wide />
            <Fact k="What they wrote" v={prospect.message} wide />
            <Fact k="Our note" v={prospect.note} wide />
          </div>

          <div className="pform-actions lead-actions">
            <button type="button" className="btn primary sm" onClick={() => setEditing(true)}>
              Edit
            </button>
            {/* Available whatever their state: a row can be onboarded under one
                record and still be the same human as another. */}
            <button type="button" className="btn sm" onClick={() => setLinking((v) => !v)}>
              {linking ? 'Cancel link' : 'Connect to a patient record…'}
            </button>
            {!prospect.waiting && (
              <button type="button" className="btn sm" onClick={() => setPaying((v) => !v)}>
                {paying ? 'Cancel' : prospect.paid ? 'Change payment' : 'Record payment'}
              </button>
            )}
            <span className="spacer" />
            <button
              type="button"
              className="btn sm ghost"
              disabled={setSpam.isPending}
              title="Junk from a stranger: leaves every list and figure; undo from the Spam bucket"
              onClick={() => setSpam.mutate({ id: prospect.id, isSpam: true })}
            >
              Mark as spam
            </button>
          </div>
          {linking && <LinkToPatient prospect={prospect} onDone={after} />}
          {paying && <RecordPayment prospect={prospect} onDone={after} />}
        </Panel>
      )}

      {/* Orders, the read prescriptions and the emails need only the
          person's id, so the person page loads them on its own: a failure
          here cannot take them with it. The upload and approval stay, since
          they need what this record says about the file on hand. */}
      {!editing && <Prescription prospect={prospect} />}
    </>
  )
}

/**
 * The lead's prescription: one PDF, JPG or PNG, uploaded by hand. Uploading again
 * replaces it. The bytes go straight to the API as the request body, and
 * opening it is a plain link, so the browser shows it in its own viewer.
 */
export function Prescription({ prospect }: { prospect: Prospect }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const review = useMutation(
    trpc.prospects.setPrescriptionReview.mutationOptions({ onSuccess: () => qc.invalidateQueries() }),
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const url = `/api/prospects/${prospect.id}/prescription`

  const send = async (init: RequestInit) => {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch(url, { credentials: 'same-origin', ...init })
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null
        throw new Error(
          res.status === 413 ? 'That file is over 10 MB.' : (body?.error ?? `Upload failed (${res.status}).`),
        )
      }
      await qc.invalidateQueries()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const upload = (file: File) => {
    if (file.size > 10 * 1024 * 1024) return setError('That file is over 10 MB.')
    void send({
      method: 'PUT',
      headers: {
        'content-type': file.type || 'application/octet-stream',
        'x-filename': encodeURIComponent(file.name),
      },
      body: file,
    })
  }

  return (
    <Panel
      title="Prescription"
      note={
        prospect.hasPrescription
          ? 'on file'
          : prospect.prescriptionCount > 0
            ? `${prospect.prescriptionCount} read, none uploaded by hand`
            : 'none yet'
      }
    >
      {/* The upload slot: one file added by hand, replaceable. A prescription
          that arrived by mail is not here; it is in the list below. Saying
          "no prescription uploaded" while the list held one misled people. */}
      <dl className="summary rx-summary">
        <dt>Upload</dt>
        <dd>
          {prospect.hasPrescription ? (
            <>
              <a href={url} target="_blank" rel="noopener">
                {prospect.prescriptionFilename ?? 'Prescription'}
              </a>
              {prospect.prescriptionUploadedAt && (
                <span className="muted"> · added {fmtDate(prospect.prescriptionUploadedAt)}</span>
              )}
            </>
          ) : (
            <span className="muted">
              {prospect.prescriptionCount > 0
                ? 'Nothing uploaded by hand; the prescription read below arrived another way'
                : 'Nothing uploaded by hand'}
            </span>
          )}
        </dd>
      </dl>

      {/* What the prescription says — the date on it, each eye, the add — read
          from the uploaded file and from any mailed in, newest first. */}
      <ScannedPrescriptions personId={prospect.id} embedded />

      <dl className="summary rx-summary">
        <dt>
          <label htmlFor={`rx-review-${prospect.id}`}>Approval</label>
        </dt>
        <dd className="rx-review-row">
          <select
            id={`rx-review-${prospect.id}`}
            className={`rx-review is-${prospect.prescriptionReview}`}
            value={prospect.prescriptionReview}
            disabled={review.isPending}
            onChange={(e) =>
              review.mutate({ id: prospect.id, status: e.target.value as PrescriptionReview })
            }
          >
            {PRESCRIPTION_REVIEW_STATUSES.map((s) => (
              <option key={s} value={s}>
                {PRESCRIPTION_REVIEW_LABELS[s]}
              </option>
            ))}
          </select>
          {prospect.prescriptionReviewedAt && (
            <span className="muted">
              set {fmtDate(prospect.prescriptionReviewedAt)}
              {prospect.prescriptionReviewedBy && <> by {prospect.prescriptionReviewedBy}</>}
            </span>
          )}
          {review.error && <span className="bad-text">{review.error.message}</span>}
        </dd>

        <dt>Comments</dt>
        <dd>
          <PrescriptionComments personId={prospect.id} />
        </dd>
      </dl>

      <div className="pform-actions rx-actions">
        <label className={`btn${prospect.hasPrescription ? '' : ' primary'}`} aria-disabled={busy}>
          {busy ? 'Uploading…' : prospect.hasPrescription ? 'Replace file…' : 'Upload PDF, JPG or PNG…'}
          <input
            type="file"
            accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
            hidden
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0]
              e.target.value = ''
              if (file) upload(file)
            }}
          />
        </label>
        {prospect.hasPrescription && (
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() => {
              if (window.confirm('Remove this prescription file?')) void send({ method: 'DELETE' })
            }}
          >
            Remove
          </button>
        )}
        {error && <span className="bad-text">{error}</span>}
      </div>

      <PrescriptionHistory personId={prospect.id} />
    </Panel>
  )
}

/**
 * Every change to one lead's prescription, newest first: who changed the
 * approval and from what to what, and who added, replaced or removed the file.
 */
function PrescriptionHistory({ personId }: { personId: string }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.prospects.prescriptionHistory.queryOptions({ id: personId }))
  const events = q.data ?? []
  if (events.length === 0) return null

  const label = (s: PrescriptionReview | null) => (s ? PRESCRIPTION_REVIEW_LABELS[s] : null)
  const describe = (e: PrescriptionHistoryEvent) => {
    if (e.kind === 'review') {
      return e.from ? (
        <>
          changed approval from <strong>{label(e.from)}</strong> to <strong>{label(e.to)}</strong>
        </>
      ) : (
        <>
          set approval to <strong>{label(e.to)}</strong>
        </>
      )
    }
    if (e.kind === 'remove') return <>removed the file <strong>{e.filename}</strong></>
    return e.replaced ? (
      <>
        replaced <strong>{e.replaced}</strong> with <strong>{e.filename}</strong>
      </>
    ) : (
      <>
        uploaded <strong>{e.filename}</strong>
      </>
    )
  }

  return (
    <section className="rx-history" aria-label="Prescription history">
      <h4>History</h4>
      <ol>
        {events.map((e, i) => (
          <li key={`${e.at}-${i}`}>
            <span className="rx-history-when">{fmtWhen(e.at)}</span>
            <span>
              <span className="rx-history-who">{e.by ?? 'Unknown'}</span> {describe(e)}
            </span>
          </li>
        ))}
      </ol>
    </section>
  )
}

/**
 * The discussion about one lead's prescription: a thread, oldest first, each
 * note signed and dated. Enter adds a line break; the button posts.
 */
function PrescriptionComments({ personId }: { personId: string }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const [draft, setDraft] = useState('')
  const listOpts = trpc.prospects.prescriptionComments.queryOptions({ id: personId })
  const q = useQuery(listOpts)
  const refresh = () => qc.invalidateQueries({ queryKey: listOpts.queryKey })
  const add = useMutation(
    trpc.prospects.addPrescriptionComment.mutationOptions({
      onSuccess: async () => {
        setDraft('')
        await refresh()
      },
    }),
  )
  const remove = useMutation(trpc.prospects.deletePrescriptionComment.mutationOptions({ onSuccess: refresh }))
  const comments = q.data ?? []

  return (
    <div className="rx-comments">
      {comments.length > 0 && (
        <ul className="rx-thread">
          {comments.map((c) => (
            <li key={c.id}>
              <p className="rx-comment-body">{c.body}</p>
              <div className="rx-comment-meta">
                <span className="muted">
                  {c.author ?? 'Unknown'} · {fmtWhen(c.at)}
                </span>
                <button
                  type="button"
                  className="rx-comment-delete"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm('Delete this comment?')) remove.mutate({ commentId: c.id })
                  }}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <form
        className="rx-comment-form"
        onSubmit={(e) => {
          e.preventDefault()
          if (draft.trim()) add.mutate({ id: personId, body: draft.trim() })
        }}
      >
        <textarea
          rows={2}
          value={draft}
          maxLength={4000}
          placeholder={comments.length ? 'Add a comment…' : 'No comments yet. Add the first one…'}
          aria-label="New prescription comment"
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="rx-comment-send">
          <button type="submit" className="btn sm primary" disabled={!draft.trim() || add.isPending}>
            {add.isPending ? 'Adding…' : 'Add comment'}
          </button>
          {(add.error || remove.error) && (
            <span className="bad-text">{(add.error ?? remove.error)?.message}</span>
          )}
        </div>
      </form>
    </div>
  )
}

/**
 * What Klaviyo sent this person, and what they did with it.
 *
 * Received and Opened on one line each, newest first. An open is listed every
 * time it happens rather than collapsed into a flag: "opened twice, sixteen
 * hours apart" is somebody going back to look, which a boolean would throw
 * away.
 */
export function EmailHistory({ prospectId }: { prospectId: string }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.prospects.emailHistory.queryOptions({ id: prospectId }))
  if (!q.data) return null
  const events = q.data

  const opens = events.filter((e) => e.kind === 'Opened Email').length
  const sent = events.filter((e) => e.kind === 'Received Email').length

  return (
    <Panel
      title="Emails"
      note={
        events.length === 0
          ? 'nothing yet'
          : `${sent} sent · ${opens === 0 ? 'none opened' : `${opens} open${opens === 1 ? '' : 's'}`}`
      }
    >
      {events.length === 0 ? (
        <p className="footnote">
          Klaviyo has sent this person nothing, or they are not on a list that sends. Joker never
          sends email itself — everything here is a copy of what Klaviyo did.
        </p>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>When</th>
              <th>What</th>
              <th>Subject</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e, i) => (
              <tr key={`${e.at}-${i}`}>
                <td className="num">{fmtWhen(e.at)}</td>
                <td>
                  <span className={e.kind === 'Opened Email' ? 'up' : e.kind === 'Bounced Email' ? 'down' : undefined}>
                    {e.kind.replace(' Email', '')}
                  </span>
                </td>
                <td>{e.subject ?? <span className="muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  )
}

/** "7 min", "2 h", "17 h", "1 d 3 h": how long somebody took to buy. */
function fmtGap(ms: number): string {
  const min = Math.round(ms / 60_000)
  if (min < 60) return `${min} min`
  const h = Math.round(min / 60)
  if (h < 24) return `${h} h`
  const d = Math.floor(h / 24)
  return h % 24 ? `${d} d ${h % 24} h` : `${d} d`
}

function median(xs: number[]): number | null {
  if (xs.length === 0) return null
  const s = [...xs].sort((a, b) => a - b)
  const m = Math.floor(s.length / 2)
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2
}

function usd(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(cents / 100)
}

/**
 * The Converted sheet's headline: how many leads bought, how many lately, how
 * long from asking to paying, and what it came to. Refunded buyers are left out.
 */
function ConvertedSummary({ converted }: { converted: Prospect[] }) {
  const bought = converted.filter((p) => !refunded(p))
  const lately = bought.filter((p) => recent(p.convertedAt, 30))
  const days = bought.map(daysToBuy).filter((d): d is number => d != null)
  const mid = median(days)
  const latelyUsd = lately.reduce((n, p) => n + (p.pricePaidUsdCents ?? (p.currency === 'USD' ? p.pricePaidCents ?? 0 : 0)), 0)
  return (
    <div className="tiles">
      <Tile k="Leads who bought" v={bought.length} d="since the waitlist began" />
      <Tile k="Last 30 days" v={lately.length} d={latelyUsd > 0 ? `${usd(latelyUsd)} paid` : 'bought in the last 30 days'} />
      <Tile k="Asking to buying" v={mid == null ? '—' : `${Math.round(mid)} d`} d="median days from joining to paying" />
      <Tile k="Bought the same week" v={days.filter((d) => d <= 7).length} d={`of ${days.length} with both dates`} />
    </div>
  )
}

/**
 * Abandoned carts that turned into a sale: who, when they left the cart, when
 * they paid, how long it took, and whether the store's reminder came first.
 * One row per cart; a cart left after the purchase is not a conversion and is
 * not listed.
 */
function CartConversions({
  carts,
  error,
  onOpenPerson,
  onOpenOrder,
}: {
  carts: AbandonedCart[] | undefined
  error: unknown
  onOpenPerson: (id: string) => void
  onOpenOrder?: (id: string) => void
}) {
  if (error) return <ErrorNote error={error as Error} />
  if (!carts) return <Loading what="cart conversions" />
  const real = carts.filter((c) => !c.isTest)
  /* Somebody who had already bought and came back for another look is not a
     lost sale, so they are out of the base the rate is taken over. */
  const base = real.filter((c) => !(c.boughtBefore && !c.boughtAfter))
  const won = real
    .filter((c) => c.afterOrder)
    .sort((a, b) => b.afterOrder!.at.localeCompare(a.afterOrder!.at))
  const gap = (c: AbandonedCart) => new Date(c.afterOrder!.at).getTime() - new Date(c.abandonedAt).getTime()
  /* Wix's reminder count does not say when each went out, only the last. When
     the last one is before the purchase, every reminder was. */
  const how = (c: AbandonedCart) => {
    const at = c.afterOrder!.at
    if (c.remindersSent > 0 && c.lastReminderAt && c.lastReminderAt <= at)
      return `After ${c.remindersSent} Wix reminder${c.remindersSent === 1 ? '' : 's'}`
    if (c.remindersSent > 1) return 'After a Wix reminder'
    return 'Came back on their own'
  }
  const mid = median(won.map(gap))
  const revenue = won.reduce((n, c) => n + (c.afterOrder!.currency === 'USD' || !c.afterOrder!.currency ? c.afterOrder!.amountCents : 0), 0)
  const rate = base.length === 0 ? 0 : Math.round((won.length / base.length) * 100)
  const money = (cents: number, cur: string | null) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: cur || 'USD', maximumFractionDigits: 0 }).format(cents / 100)

  return (
    <>
      <div className="tiles">
        <Tile k="Carts that sold" v={won.length} d={`of ${base.length} carts left by non-customers`} />
        <Tile k="Recovery rate" v={`${rate}%`} d="came back and paid" />
        <Tile k="Time to buy" v={mid == null ? '—' : fmtGap(mid)} d="median, from leaving the cart" />
        <Tile k="Recovered" v={usd(revenue)} d={`${won.filter((c) => how(c).startsWith('After')).length} after a reminder`} />
      </div>
      <Panel title="Cart conversions" note={`${won.length} carts`}>
        {won.length === 0 ? (
          <p className="footnote">No abandoned cart has turned into a sale yet.</p>
        ) : (
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Left the cart</th>
                  <th>Bought</th>
                  <th className="r">Took</th>
                  <th>Wix order</th>
                  <th className="r">Paid</th>
                  <th>How</th>
                </tr>
              </thead>
              <tbody>
                {won.map((c) => {
                  const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unnamed'
                  const o = c.afterOrder!
                  return (
                    <tr key={c.id}>
                      <td>
                        {c.personId ? (
                          <button type="button" className="linklike" onClick={() => onOpenPerson(c.personId!)}>
                            <strong>{name}</strong>
                          </button>
                        ) : (
                          <strong>{name}</strong>
                        )}
                        <br />
                        <span className="muted sub-line">{[c.leadCode, c.email].filter(Boolean).join(' · ')}</span>
                      </td>
                      <td className="num">{fmtWhen(c.abandonedAt)}</td>
                      <td className="num">{fmtWhen(o.at)}</td>
                      <td className="r num">{fmtGap(gap(c))}</td>
                      <td className="num">
                        {o.number && onOpenOrder ? (
                          <button type="button" className="linklike" onClick={() => onOpenOrder(o.id)}>
                            #{o.number}
                          </button>
                        ) : o.number ? (
                          `#${o.number}`
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="r num">{money(o.amountCents, o.currency)}</td>
                      <td>
                        {how(c)}
                        {c.wixStatus === 'RECOVERED' && (
                          <>
                            <br />
                            <span className="muted sub-line">paid through the saved cart</span>
                          </>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="footnote">
          A cart counts as sold when the same email pays for an order after leaving it. The rate leaves
          out our own tests and people who had already bought before leaving the cart. Wix reports
          only how many reminders went out and when the last one did, so &ldquo;after a
          reminder&rdquo; is exact only when the last reminder came before the purchase.
        </p>
      </Panel>
    </>
  )
}

/** Paid and then refunded: listed, marked, and left out of the counts. */
function refunded(p: Prospect): boolean {
  return p.stage === 'churned_refunded'
}

/** Paid within the last `days` days. */
function recent(iso: string | null, days: number): boolean {
  return iso != null && Date.now() - new Date(iso).getTime() <= days * 86_400_000
}

/** Whole days from joining the waitlist to paying; null when either is unknown or out of order. */
function daysToBuy(p: Prospect): number | null {
  if (!p.joinedAt || !p.convertedAt) return null
  const d = Math.floor((new Date(p.convertedAt).getTime() - new Date(p.joinedAt).getTime()) / 86_400_000)
  return d < 0 ? null : d
}

/**
 * Every email Klaviyo sent one lead, unfolded under their row: one line per
 * email received, newest first, with whether they opened or clicked it. An
 * open counts for the email it came after with the same subject, so a
 * newsletter sent twice is not marked opened twice by one look.
 */
function LeadEmails({ personId }: { personId: string }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.prospects.emailHistory.queryOptions({ id: personId }))
  if (q.error) return <ErrorNote error={q.error} />
  if (!q.data) return <Loading what="emails" />

  const events = [...q.data].sort((a, b) => a.at.localeCompare(b.at))
  const received = events.filter((e) => e.kind === 'Received Email' || e.kind === 'Bounced Email')
  const did = (kind: string, e: (typeof events)[number], i: number) => {
    const next = received.slice(i + 1).find((n) => n.subject === e.subject)
    return events.some((x) => x.kind === kind && x.subject === e.subject && x.at >= e.at && (!next || x.at < next.at))
  }
  const lines = received.map((e, i) => ({ ...e, opened: did('Opened Email', e, i), clicked: did('Clicked Email', e, i) })).reverse()

  return (
    <table className="data compact">
      <thead>
        <tr>
          <th>Sent</th>
          <th>Subject</th>
          <th>Opened</th>
          <th>Clicked</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((e, i) => (
          <tr key={`${e.at}-${i}`}>
            <td className="num">{fmtWhen(e.at)}</td>
            <td>
              {e.subject ?? <span className="muted">—</span>}
              {e.kind === 'Bounced Email' && <span className="down"> · bounced</span>}
            </td>
            <td>{e.opened ? <span className="up">Yes</span> : <span className="muted">No</span>}</td>
            <td>{e.clicked ? <span className="up">Yes</span> : <span className="muted">No</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** "1 Oct, 11:25" — the time matters here, so the date alone will not do. */
function fmtWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const yesNo = (v: boolean | null) => (v == null ? null : v ? 'Yes' : 'No')

/** One labelled fact in a grid; nothing at all when there is no value. */
function Fact({ k, v, copy, wide }: { k: string; v: string | null | undefined; copy?: string | null; wide?: boolean }) {
  if (!v) return null
  return (
    <div className={`fact${wide ? ' wide' : ''}`}>
      <div className="k">{k}</div>
      <div className="v">{copy ? <CopyValue v={v} copy={copy} /> : v}</div>
    </div>
  )
}

/** A value that copies itself when pressed, saying so once it has. */
function CopyValue({ v, copy }: { v: string; copy: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      className="copyable"
      title={`Copy ${copy}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(copy)
        } catch {
          return
        }
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1400)
      }}
    >
      {v}
      <span className={`copied${copied ? ' on' : ''}`} aria-live="polite">
        {copied ? 'Copied' : 'Copy'}
      </span>
    </button>
  )
}


/* -------------------------------- editing -------------------------------- */

/**
 * Hand corrections. These rows are whatever somebody typed into a web form or a
 * spreadsheet cell — "Boaz Hecht/Sivan" is a couple sharing one submission where
 * only one of them converted — so every field is editable, and clearing one
 * clears it.
 */
function ProspectEditor({ prospect, onDone }: { prospect: Prospect; onDone: () => void }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const [form, setForm] = useState<ProspectEdit>(() => ({
    id: prospect.id,
    firstName: prospect.firstName,
    lastName: prospect.lastName,
    email: prospect.email,
    phone: prospect.phone,
    source: prospect.source,
    location: prospect.location,
    whoIAm: prospect.whoIAm,
    message: prospect.message,
    note: prospect.note,
    joinedAt: prospect.joinedAt ? prospect.joinedAt.slice(0, 10) : null,
  }))

  const save = useMutation(
    trpc.prospects.update.mutationOptions({
      onSuccess: async () => {
        await qc.invalidateQueries()
        onDone()
      },
    }),
  )

  const set = <K extends keyof ProspectEdit>(k: K, v: ProspectEdit[K]) =>
    setForm((f) => ({ ...f, [k]: v }))
  const text = (v: string) => (v.trim() === '' ? null : v)

  return (
    <Panel title="Edit">
      <form
        className="pform"
        onSubmit={(e) => {
          e.preventDefault()
          save.mutate({
            ...form,
            /* A date input gives a bare day and the column is a timestamp; midday
               keeps the day from moving under a timezone conversion. */
            /* Saving without touching the date keeps the exact time they
               joined; a new date has no time, and midday keeps it on its day
               whatever the time zone. */
            joinedAt: !form.joinedAt
              ? null
              : prospect.joinedAt && prospect.joinedAt.slice(0, 10) === form.joinedAt
                ? prospect.joinedAt
                : `${form.joinedAt}T12:00:00Z`,
          })
        }}
      >
        <fieldset>
          <legend>Who</legend>
          <Field label="First name" value={form.firstName} onChange={(v) => set('firstName', v)} />
          <Field label="Last name" value={form.lastName} onChange={(v) => set('lastName', v)} />
          <Field label="Email" type="email" value={form.email} onChange={(v) => set('email', v)} />
          <Field label="Phone" value={form.phone} onChange={(v) => set('phone', v)} />
        </fieldset>

        <fieldset>
          <legend>Where they came from</legend>
          <Field
            label="Source"
            value={form.source}
            onChange={(v) => set('source', v)}
            placeholder="Website, Klaviyo waitlist…"
          />
          <Field label="Location" value={form.location} onChange={(v) => set('location', v)} />
          <label className="pfield">
            <span>Asked on</span>
            <DateParts
              value={form.joinedAt ?? null}
              earliestYear={2021}
              onChange={(iso) => set('joinedAt', iso)}
            />
          </label>
          <Field label="Who they are" value={form.whoIAm} onChange={(v) => set('whoIAm', v)} />
        </fieldset>

        <fieldset>
          <legend>What was said</legend>
          <label className="pfield wide">
            <span>What they wrote</span>
            <textarea
              rows={2}
              value={form.message ?? ''}
              onChange={(e) => set('message', text(e.target.value))}
            />
          </label>
          <label className="pfield wide">
            <span>Our note</span>
            <textarea
              rows={2}
              value={form.note ?? ''}
              onChange={(e) => set('note', text(e.target.value))}
            />
          </label>
        </fieldset>

        <div className="pform-actions">
          <button type="submit" className="btn primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn" onClick={onDone}>
            Cancel
          </button>
          {save.error && <span className="bad-text">{save.error.message}</span>}
        </div>
      </form>
    </Panel>
  )
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string
  value: string | null
  onChange: (v: string | null) => void
  type?: string
  placeholder?: string
}) {
  return (
    <label className="pfield">
      <span>{label}</span>
      <input
        type={type}
        value={value ?? ''}
        placeholder={placeholder ?? ''}
        onChange={(e) => onChange(e.target.value.trim() === '' ? null : e.target.value)}
      />
    </label>
  )
}

/**
 * Joins a waiting prospect to the patient record they already have.
 *
 * Needed because the two can carry different email addresses — somebody joins a
 * waitlist on one and pays on another — which is the one case automatic matching
 * cannot cover. Searching by name is the way in, since the name is usually the
 * only thing the two records share.
 */
function LinkToPatient({ prospect, onDone }: { prospect: Prospect; onDone: () => void }) {
  const trpc = useTRPC()
  const [term, setTerm] = useState(prospect.name ?? '')

  const found = useQuery({
    ...trpc.people.list.queryOptions({
      search: term,
      sort: 'name',
      dir: 'asc',
      limit: 8,
      cursor: 0,
    }),
    enabled: term.trim().length >= 2,
  })

  const link = useMutation(trpc.prospects.linkToPatient.mutationOptions({ onSuccess: onDone }))
  const buyFor = useMutation(trpc.prospects.setPurchasedFor.mutationOptions({ onSuccess: onDone }))

  return (
    <div className="link-patient">
      <label className="pfield">
        <span>Find the patient record</span>
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Name or email"
          autoFocus
        />
      </label>
      {term.trim().length < 2 ? (
        <p className="footnote">Type at least two characters.</p>
      ) : !found.data ? (
        <Loading what="patients" />
      ) : found.data.items.length === 0 ? (
        <p className="footnote">No patient matches that.</p>
      ) : (
        <ul className="candidates">
          {found.data.items.map((c) => (
            <li key={c.id}>
              {/* Two different relationships, and picking the wrong one is not
                  recoverable by eye: "Same person" folds this lead into that
                  patient, while "Bought for them" keeps both and moves only the
                  money. */}
              <button
                type="button"
                className="btn ghost sm"
                disabled={link.isPending || buyFor.isPending}
                onClick={() => {
                  if (window.confirm(`Are ${prospect.name ?? 'this lead'} and ${c.name ?? 'this patient'} the same person? Their two records will be merged into one.`)) {
                    link.mutate({ prospectId: prospect.id, patientId: c.id })
                  }
                }}
              >
                Same person
              </button>
              <button
                type="button"
                className="btn ghost sm"
                disabled={link.isPending || buyFor.isPending}
                onClick={() => buyFor.mutate({ buyerId: prospect.id, patientId: c.id })}
              >
                Bought for them
              </button>
              <span>
                <strong>{c.name ?? 'Unnamed'}</strong>{' '}
                <span className="muted">{c.email ?? 'no email'}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="footnote">
        Linking keeps the patient record — it holds the sessions and the payment — and moves this
        lead&rsquo;s id, ask date and notes onto it. This row is marked superseded rather than
        deleted, so the merge can be undone.
      </p>
      {link.error && <p className="bad-text">{link.error.message}</p>}
    </div>
  )
}

/**
 * Records what somebody paid, when the money did not come through the Wix store.
 *
 * Three currencies because three are what people actually pay in — dollars,
 * shekels and francs. They are never summed with each other anywhere; each
 * amount stays in the currency it was taken in.
 */
function RecordPayment({ prospect, onDone }: { prospect: Prospect; onDone: () => void }) {
  const trpc = useTRPC()
  const [amount, setAmount] = useState(
    prospect.pricePaidCents != null ? String(prospect.pricePaidCents / 100) : '',
  )
  const [currency, setCurrency] = useState<PaymentCurrency>(
    (PAYMENT_CURRENCIES as readonly string[]).includes(prospect.currency ?? '')
      ? (prospect.currency as PaymentCurrency)
      : 'USD',
  )
  const [method, setMethod] = useState<PaymentMethod>(
    (PAYMENT_METHODS as readonly string[]).includes(prospect.paymentMethod ?? '')
      ? (prospect.paymentMethod as PaymentMethod)
      : prospect.practiceName
        ? 'optometrist'
        : 'wix',
  )
  /* No payment date of their own yet, so the day their record was created stands
     in for it — which is the best available answer until a real receipt turns up,
     and stops the field opening on today for a purchase made months ago. */
  const [paidOn, setPaidOn] = useState(
    (prospect.convertedAt ?? prospect.recordCreatedAt ?? new Date().toISOString()).slice(0, 10),
  )

  const save = useMutation(trpc.prospects.recordPayment.mutationOptions({ onSuccess: onDone }))
  const value = Number(amount.replace(/[^\d.]/g, ''))
  const usable = amount.trim() !== '' && Number.isFinite(value) && paidOn !== ''

  return (
    <form
      className="link-patient"
      onSubmit={(e) => {
        e.preventDefault()
        if (!usable) return
        save.mutate({
          personId: prospect.id,
          amount: value,
          currency,
          method,
          paidOn: `${paidOn}T12:00:00Z`,
        })
      }}
    >
      <div className="pform">
        <fieldset>
          <legend>Payment</legend>
          <label className="pfield">
            <span>Amount</span>
            <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="1000" />
          </label>
          <label className="pfield">
            <span>Currency</span>
            <select className="sel" value={currency} onChange={(e) => setCurrency(e.target.value as PaymentCurrency)}>
              {PAYMENT_CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {CURRENCY_LABELS[c]}
                </option>
              ))}
            </select>
          </label>
          <label className="pfield">
            <span>How they paid</span>
            <select className="sel" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LABELS[m]}
                </option>
              ))}
            </select>
          </label>
          <label className="pfield">
            <span>Paid on</span>
            <DateParts
              value={paidOn || null}
              earliestYear={2021}
              onChange={(iso) => setPaidOn(iso ?? '')}
            />
          </label>
        </fieldset>
        <div className="pform-actions">
          <button type="submit" className="btn primary" disabled={!usable || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save payment'}
          </button>
          <button type="button" className="btn" onClick={onDone}>
            Cancel
          </button>
          {save.error && <span className="bad-text">{save.error.message}</span>}
        </div>
      </div>
      <p className="footnote">
        Recorded as an order so it counts everywhere revenue does. Saving again replaces this
        payment rather than adding a second one.
      </p>
    </form>
  )
}

/**
 * People who started a checkout on the store and did not pay, newest first,
 * with everything the store holds about them and the link that takes them
 * back to finish. Our own test checkouts are hidden unless asked for. A cart
 * is a lead in the plain sense — somebody wanted it — but it creates no person
 * and changes no count; a name links to the record when Joker knows them.
 */
function AbandonedCarts({
  carts,
  error,
  onOpenPerson,
}: {
  carts: AbandonedCart[] | undefined
  error: unknown
  onOpenPerson: (id: string) => void
}) {
  const [withTests, setWithTests] = useState(false)
  const [search, setSearch] = useState('')
  if (error) return <ErrorNote error={error as Error} />
  if (!carts) return <Loading what="abandoned carts" />
  const term = search.trim().toLowerCase()
  const list = carts
    .filter((c) => withTests || !c.isTest)
    .filter(
      (c) =>
        !term ||
        [c.firstName, c.lastName, c.email, c.phone, c.address, c.products].some((v) => (v ?? '').toLowerCase().includes(term)),
    )
  const real = carts.filter((c) => !c.isTest)
  const money = (cents: number, cur: string | null) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currencyDisplay: 'narrowSymbol', currency: cur || 'USD', maximumFractionDigits: 0 }).format(cents / 100)
  return (
    <>
      <div className="tiles">
        <Tile k="Abandoned carts" v={real.length} d="not counting our own tests" />
        <Tile k="Bought afterwards" v={real.filter((c) => c.boughtAfter).length} d="came back and paid" />
        <Tile k="Had bought before" v={real.filter((c) => c.boughtBefore && !c.boughtAfter).length} d="customers starting another purchase" />
        <Tile k="Reminded by the store" v={real.filter((c) => c.remindersSent > 0).length} d="at least one reminder email" />
      </div>
      <div className="toolbar prospect-search">
        <input type="search" placeholder="Name, email, phone, product…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search abandoned carts" />
        <label className="ctl check inline-check">
          <input type="checkbox" checked={withTests} onChange={(e) => setWithTests(e.target.checked)} />
          <span>Show our own tests</span>
        </label>
        <a className="btn primary export" href="/api/export/abandoned.csv" download>
          ↓ Export CSV
        </a>
      </div>
      <Panel title="Abandoned carts" note={`${list.length} of ${withTests ? carts.length : real.length}`}>
        {list.length === 0 ? (
          <p className="footnote">{search ? 'Nobody matches that search.' : 'No abandoned carts yet.'}</p>
        ) : (
          <div className="scroll-x">
            <table className="data">
              <thead>
                <tr>
                  <th>Abandoned</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Where</th>
                  <th>In the cart</th>
                  <th className="r">Total</th>
                  <th className="r">Reminders</th>
                  <th>Bought?</th>
                  <th>Recover</th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => {
                  const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unnamed'
                  return (
                    <tr key={c.id} className={c.isTest ? 'dim' : undefined}>
                      <td className="num">
                        {fmtDate(c.abandonedAt)}
                        <br />
                        <span className="muted sub-line">{new Date(c.abandonedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                      </td>
                      <td>
                        {c.personId ? (
                          <button type="button" className="linklike" onClick={() => onOpenPerson(c.personId!)}>
                            <strong>{name}</strong>
                          </button>
                        ) : (
                          <strong>{name}</strong>
                        )}
                        {c.leadCode && (
                          <>
                            <br />
                            <span className="muted sub-line">{c.leadCode}</span>
                          </>
                        )}
                        {c.isTest && <span className="pill test">Test</span>}
                      </td>
                      <td>{c.email ?? <span className="muted">—</span>}</td>
                      <td className="num">{displayPhone(c.phone) || c.phone || <span className="muted">—</span>}</td>
                      <td>{c.address ?? <span className="muted">—</span>}</td>
                      <td>
                        {c.products || <span className="muted">—</span>}
                        {c.discountCodes.length > 0 && (
                          <>
                            <br />
                            <span className="muted sub-line">code {c.discountCodes.join(', ')}</span>
                          </>
                        )}
                      </td>
                      <td className="r num">{money(c.totalCents, c.currency)}</td>
                      <td className="r num">{c.remindersSent || <span className="muted">0</span>}</td>
                      <td>
                        {c.boughtAfter ? (
                          <span className="pill pay-paid">Bought later</span>
                        ) : c.boughtBefore ? (
                          <span className="muted">bought before</span>
                        ) : (
                          <span className="muted">No</span>
                        )}
                      </td>
                      <td>
                        {c.recoveryUrl ? (
                          <a href={c.recoveryUrl} target="_blank" rel="noopener">
                            Checkout ↗
                          </a>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="footnote">
          From the Wix store, every hour. Wix keeps only recent carts, so Joker keeps each one it has
          seen. <strong>Bought later</strong> is read from orders, so a cart that turns into a sale
          says so as soon as the order lands. <strong>Checkout ↗</strong> is the store&rsquo;s own
          recovery link: it opens the cart they left, ready to pay.
        </p>
      </Panel>
    </>
  )
}
